import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userSession } from '../../../services/localStorageService';
import { canadianCities } from '../../../utils/canadianCities';

const ProfileVisibility = ({ userRole, onSave }) => {
  const currentUser = userSession?.get();
  const [settings, setSettings] = useState({
    profileActive: true,
    visibilityLevel: 'selective',
    showInFeed: true,
    allowDirectContact: false,
    showAvailability: true,
    showSalaryRange: false,
    showLocation: true,
    showIndustry: true,
    showYearsExperience: true,
    limitToIndustries: [],
    limitToLocations: [],
    excludeCompanies: []
  });

  useEffect(() => {
    const savedPrefs = localStorage.getItem(`preferences_visibility_${currentUser?.id}`);
    if (savedPrefs) {
      setSettings(JSON.parse(savedPrefs));
    }
  }, [currentUser?.id]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSelectChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    localStorage.setItem(`preferences_visibility_${currentUser?.id}`, JSON.stringify(settings));
    onSave?.('Profile visibility settings saved successfully');
  };

  const visibilityOptions = [
    { 
      value: 'public', 
      label: 'Public', 
      description: 'Visible to all verified employers' 
    },
    { 
      value: 'selective', 
      label: 'Selective', 
      description: 'Visible only to employers matching your criteria' 
    },
    { 
      value: 'private', 
      label: 'Private', 
      description: 'Hidden from all searches - only visible to saved matches' 
    }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology' },
    { value: 'construction', label: 'Construction' },
    { value: 'realestate', label: 'Real Estate' },
    { value: 'finance', label: 'Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'manufacturing', label: 'Manufacturing' }
  ];

  const locationOptions = canadianCities;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Profile Visibility</h2>
        <p className="text-sm text-[#6B7280]">
          {userRole === 'candidate' ?'Control who can discover your profile and what information they can see' :'Manage your company profile visibility and employer branding settings'
          }
        </p>
      </div>

      {/* Profile Status */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Shield" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Profile Status</h3>
        </div>
        
        <Checkbox
          checked={settings?.profileActive}
          onChange={() => handleToggle('profileActive')}
          label={`${settings?.profileActive ? 'Profile Active' : 'Profile Inactive'}`}
          description={settings?.profileActive 
            ? 'Your profile is visible and discoverable' :'Your profile is hidden from all searches'
          }
        />

        {settings?.profileActive && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
            <Icon name="CheckCircle" size={20} color="#16a34a" />
            <div className="text-sm text-green-900">
              <p className="font-medium">Your profile is active and visible</p>
            </div>
          </div>
        )}

        {!settings?.profileActive && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <Icon name="AlertCircle" size={20} color="#f59e0b" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">Your profile is currently hidden</p>
              <p className="text-amber-700 mt-1">You won't appear in searches or receive new match suggestions</p>
            </div>
          </div>
        )}
      </div>

      {settings?.profileActive && (
        <>
          {/* Visibility Level */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
              <Icon name="Target" size={20} color="#5B2DFF" />
              <h3 className="text-lg font-semibold text-[#111827]">Visibility Level</h3>
            </div>
            
            <Select
              label="Who can see your profile"
              options={visibilityOptions}
              value={settings?.visibilityLevel}
              onChange={(value) => handleSelectChange('visibilityLevel', value)}
            />
          </div>

          {/* Discovery Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
              <Icon name="Search" size={20} color="#5B2DFF" />
              <h3 className="text-lg font-semibold text-[#111827]">Discovery Settings</h3>
            </div>
            
            <Checkbox
              checked={settings?.showInFeed}
              onChange={() => handleToggle('showInFeed')}
              label="Show in opportunity feed"
              description="Appear in employer search results and recommendations"
            />

            {userRole === 'candidate' && (
              <Checkbox
                checked={settings?.allowDirectContact}
                onChange={() => handleToggle('allowDirectContact')}
                label="Allow direct contact requests"
                description="Employers can request to contact you directly"
              />
            )}
          </div>

          {/* Profile Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
              <Icon name="FileText" size={20} color="#5B2DFF" />
              <h3 className="text-lg font-semibold text-[#111827]">Visible Information</h3>
            </div>
            
            <Checkbox
              checked={settings?.showAvailability}
              onChange={() => handleToggle('showAvailability')}
              label="Show availability status"
              description="Display whether you're actively looking or open to opportunities"
            />

            <Checkbox
              checked={settings?.showLocation}
              onChange={() => handleToggle('showLocation')}
              label="Show location"
              description="Display your general location (city/region)"
            />

            <Checkbox
              checked={settings?.showIndustry}
              onChange={() => handleToggle('showIndustry')}
              label="Show industry"
              description="Display your industry and sector information"
            />

            <Checkbox
              checked={settings?.showYearsExperience}
              onChange={() => handleToggle('showYearsExperience')}
              label="Show years of experience"
              description="Display your total years of professional experience"
            />

            {userRole === 'candidate' && (
              <Checkbox
                checked={settings?.showSalaryRange}
                onChange={() => handleToggle('showSalaryRange')}
                label="Show salary expectations"
                description="Display your expected salary range to potential employers"
              />
            )}
          </div>

          {/* Targeting Filters */}
          {userRole === 'candidate' && settings?.visibilityLevel === 'selective' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
                <Icon name="Filter" size={20} color="#5B2DFF" />
                <h3 className="text-lg font-semibold text-[#111827]">Targeting Filters</h3>
              </div>
              
              <Select
                label="Limit visibility to specific industries"
                description="Only show your profile to employers in selected industries"
                options={industryOptions}
                value={settings?.limitToIndustries}
                onChange={(value) => handleSelectChange('limitToIndustries', value)}
                multiple
              />

              <Select
                label="Limit visibility to specific locations"
                description="Only show your profile to employers in selected locations"
                options={locationOptions}
                value={settings?.limitToLocations}
                onChange={(value) => handleSelectChange('limitToLocations', value)}
                multiple
              />
            </div>
          )}

          {/* Preview */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <Icon name="Shield" size={24} color="#5B2DFF" />
              <div>
                <h4 className="font-semibold text-[#111827] mb-1">Profile Preview</h4>
                <p className="text-sm text-[#6B7280]">This is how your profile appears to others</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="User" size={16} color="#6B7280" />
                <span className="text-sm text-[#111827]">Anonymous Professional</span>
              </div>
              {settings?.showIndustry && (
                <div className="flex items-center gap-2">
                  <Icon name="Briefcase" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">Industry visible</span>
                </div>
              )}
              {settings?.showLocation && (
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">Location visible</span>
                </div>
              )}
              {settings?.showYearsExperience && (
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={16} color="#6B7280" />
                  <span className="text-sm text-[#6B7280]">Experience level visible</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="pt-6 border-t border-[#E5E7EB] flex justify-end">
        <Button
          onClick={handleSave}
          iconName="Save"
          className="bg-gradient-to-r from-[#5B2DFF] to-[#7C3FFF]"
        >
          Save Visibility Settings
        </Button>
      </div>
    </div>
  );
};

export default ProfileVisibility;